import DOMPurify from "dompurify";
import * as THREE from "three";

const ICON_TYPES = [
  { type: "pin", label: "Pin" },
  { type: "video", label: "Video" },
  { type: "image", label: "Image" },
  { type: "info", label: "Info" },
  { type: "web", label: "Web" },
];

// Helper function to create a safe fallback texture
function createSafeFallbackTexture(size: number = 128): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = "#2196f3";
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, 2 * Math.PI);
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;

  return texture;
}

// Helper function to validate canvas and context
function validateCanvasContext(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D | null,
): boolean {
  if (!canvas || !ctx) {
    return false;
  }

  // Check if canvas dimensions are valid
  if (
    canvas.width <= 0 ||
    canvas.height <= 0 ||
    Number.isNaN(canvas.width) ||
    Number.isNaN(canvas.height)
  ) {
    return false;
  }

  // Test basic canvas functionality
  try {
    ctx.save();
    ctx.restore();
    return true;
  } catch (_error) {
    return false;
  }
}

export function createHotspotIconTexture(color: string, iconType: string) {
  const size = 128;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.error("Failed to get 2D context for hotspot icon texture");
      return createSafeFallbackTexture(size);
    }

    // Add robust null check for canvas context
    if (!validateCanvasContext(canvas, ctx)) {
      console.error("Failed to get valid canvas context for hotspot icon texture");
      return createSafeFallbackTexture(size);
    }

    // Validate color parameter
    const validColor =
      color && typeof color === "string" && color.trim() !== "" ? color : "#2196f3";

    // Validate iconType parameter
    const validIconType = iconType && typeof iconType === "string" ? iconType : "pin";

    // Enable antialiasing with error handling
    try {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
    } catch (_error) {
      // Ignore antialiasing errors, continue without it
    }

    // Clear canvas first
    ctx.clearRect(0, 0, size, size);

    // Draw circular background with better anti-aliasing
    try {
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 4, 0, 2 * Math.PI);
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = validColor;
      ctx.stroke();
    } catch (error) {
      console.error("Error drawing background circle:", error);
      return createSafeFallbackTexture(size);
    }

    // Draw the selected icon in the center
    ctx.save();
    try {
      ctx.translate(size / 2, size / 2);
      ctx.scale(4.5, 4.5);

      switch (validIconType) {
        case "video":
          ctx.beginPath();
          ctx.arc(0, 0, 18, 0, 2 * Math.PI);
          ctx.fillStyle = validColor;
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(-8, -10);
          ctx.lineTo(12, 0);
          ctx.lineTo(-8, 10);
          ctx.closePath();
          ctx.fillStyle = "#fff";
          ctx.fill();
          break;
        case "image":
          ctx.beginPath();
          ctx.rect(-16, -12, 32, 24);
          ctx.fillStyle = validColor;
          ctx.fill();
          ctx.strokeStyle = "#fff";
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(-8, -6, 3, 0, 2 * Math.PI);
          ctx.fillStyle = "#fff";
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(-16, 12);
          ctx.lineTo(-4, 2);
          ctx.lineTo(6, 8);
          ctx.lineTo(16, -12);
          ctx.lineTo(16, 12);
          ctx.closePath();
          ctx.fillStyle = "#fff";
          ctx.globalAlpha = 0.8;
          ctx.fill();
          ctx.globalAlpha = 1.0;
          break;
        case "info":
          ctx.beginPath();
          ctx.arc(0, 0, 18, 0, 2 * Math.PI);
          ctx.fillStyle = validColor;
          ctx.fill();
          ctx.font = "bold 28px sans-serif";
          ctx.fillStyle = "#fff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("i", 0, 3);
          break;
        case "web":
          ctx.beginPath();
          ctx.arc(0, 0, 18, 0, 2 * Math.PI);
          ctx.fillStyle = validColor;
          ctx.fill();
          ctx.strokeStyle = "#fff";
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(0, 0, 14, 0, 2 * Math.PI);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(0, 0, 14, Math.PI / 2, (3 * Math.PI) / 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(-14, 0);
          ctx.lineTo(14, 0);
          ctx.stroke();
          break;
        case "pin":
          // Only render the 📍 emoji if explicitly chosen, otherwise leave empty
          break;
        default:
          // Do not render any icon for unrecognized types
          break;
      }
    } catch (error) {
      console.error("Error drawing icon on canvas:", error);
    } finally {
      ctx.restore();
    }

    // Validate canvas content before creating texture
    try {
      const imageData = ctx.getImageData(0, 0, size, size);
      if (!imageData || !imageData.data || imageData.data.length === 0) {
        console.error("Invalid canvas image data");
        return createSafeFallbackTexture(size);
      }
    } catch (error) {
      console.error("Error validating canvas image data:", error);
      return createSafeFallbackTexture(size);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    return texture;
  } catch (error) {
    console.error("Error creating hotspot icon texture:", error);
    return createSafeFallbackTexture(size);
  }
}

// Cache for SVG textures to avoid recreating the same textures
const svgTextureCache = new Map<string, THREE.Texture>();

// Function to clear the texture cache and dispose of textures
export function clearSVGTextureCache() {
  svgTextureCache.forEach((texture) => {
    texture.dispose();
  });
  svgTextureCache.clear();
}

// Helper function to sanitize and validate SVG string
function sanitizeAndValidateSVG(svgString: string): { isValid: boolean; sanitizedSvg: string } {
  if (!svgString || typeof svgString !== "string") {
    return { isValid: false, sanitizedSvg: "" };
  }

  const trimmed = svgString.trim();
  if (trimmed === "") {
    return { isValid: false, sanitizedSvg: "" };
  }

  // Basic SVG structure validation
  if (!trimmed.includes("<svg")) {
    return { isValid: false, sanitizedSvg: "" };
  }

  try {
    // Configure DOMPurify for SVG sanitization
    const purifyConfig = {
      // Allow SVG elements and attributes
      ALLOWED_TAGS: [
        "svg",
        "g",
        "path",
        "circle",
        "rect",
        "line",
        "polyline",
        "polygon",
        "ellipse",
        "text",
        "tspan",
        "defs",
        "linearGradient",
        "radialGradient",
        "stop",
        "mask",
        "clipPath",
        "pattern",
        "image",
        "use",
        "symbol",
      ],
      ALLOWED_ATTR: [
        "viewBox",
        "width",
        "height",
        "x",
        "y",
        "cx",
        "cy",
        "r",
        "rx",
        "ry",
        "d",
        "points",
        "fill",
        "stroke",
        "stroke-width",
        "stroke-linecap",
        "stroke-linejoin",
        "stroke-dasharray",
        "opacity",
        "transform",
        "id",
        "class",
        "style",
        "x1",
        "y1",
        "x2",
        "y2",
        "href",
        "xlink:href",
        "gradientUnits",
        "gradientTransform",
        "offset",
        "stop-color",
        "stop-opacity",
        "maskUnits",
        "clipPathUnits",
        "patternUnits",
        "patternTransform",
        "font-family",
        "font-size",
        "font-weight",
        "text-anchor",
        "dominant-baseline",
      ],
      // Remove dangerous attributes
      FORBID_ATTR: [
        "onload",
        "onerror",
        "onclick",
        "onmouseover",
        "onmouseout",
        "onmousedown",
        "onmouseup",
        "onfocus",
        "onblur",
        "onchange",
        "onsubmit",
        "onreset",
        "onselect",
        "onkeydown",
        "onkeyup",
        "onkeypress",
        "onabort",
        "oncanplay",
        "oncanplaythrough",
        "ondurationchange",
        "onemptied",
        "onended",
        "onerror",
        "onloadeddata",
        "onloadedmetadata",
        "onloadstart",
        "onpause",
        "onplay",
        "onplaying",
        "onprogress",
        "onratechange",
        "onseeked",
        "onseeking",
        "onstalled",
        "onsuspend",
        "ontimeupdate",
        "onvolumechange",
        "onwaiting",
      ],
      // Remove script tags and event handlers
      FORBID_TAGS: ["script", "object", "embed", "iframe", "link", "meta"],
      // Remove dangerous protocols
      ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      // Sanitize CSS in style attributes
      SANITIZE_DOM: true,
      // Keep SVG namespace
      KEEP_CONTENT: true,
      // Remove comments and CDATA sections
      REMOVE_COMMENTS: true,
      // Remove empty attributes
      REMOVE_EMPTY_ATTR: true,
    };

    // Sanitize the SVG content
    const sanitizedSvg = DOMPurify.sanitize(trimmed, purifyConfig);

    // Additional validation after sanitization
    if (!sanitizedSvg || sanitizedSvg.trim() === "") {
      return { isValid: false, sanitizedSvg: "" };
    }

    // Check for remaining dangerous patterns
    const dangerousPatterns = [
      /javascript:/i,
      /data:text\/html/i,
      /vbscript:/i,
      /on\w+\s*=/i,
      /<script/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /<link/i,
      /<meta/i,
      /expression\s*\(/i,
      /url\s*\(/i,
      /@import/i,
      /behavior\s*:/i,
      /-moz-binding/i,
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(sanitizedSvg)) {
        console.warn("Potentially dangerous content detected in SVG after sanitization:", pattern);
        return { isValid: false, sanitizedSvg: "" };
      }
    }

    // Ensure the sanitized SVG still contains valid SVG structure
    if (!sanitizedSvg.includes("<svg") || !sanitizedSvg.includes("</svg>")) {
      return { isValid: false, sanitizedSvg: "" };
    }

    return { isValid: true, sanitizedSvg: sanitizedSvg.trim() };
  } catch (error) {
    console.error("Error sanitizing SVG content:", error);
    return { isValid: false, sanitizedSvg: "" };
  }
}

export function createSVGTexture(svgString: string): Promise<THREE.Texture> {
  // Sanitize and validate SVG content
  const { isValid, sanitizedSvg } = sanitizeAndValidateSVG(svgString);
  if (!isValid) {
    console.warn("Invalid or unsafe SVG string provided to createSVGTexture");
    return Promise.resolve(createSafeFallbackTexture(128));
  }

  const trimmedSvg = sanitizedSvg;

  // Check cache first (skip cache for gradients with unique IDs to avoid conflicts)
  const hasUniqueGradient = trimmedSvg.includes("glow_");
  if (!hasUniqueGradient && svgTextureCache.has(trimmedSvg)) {
    const cachedTexture = svgTextureCache.get(trimmedSvg);
    // Validate cached texture
    if (cachedTexture?.image) {
      return Promise.resolve(cachedTexture);
    }
    // Remove invalid cached texture
    svgTextureCache.delete(trimmedSvg);
  }

  return new Promise((resolve, _reject) => {
    try {
      const cleanSvg = trimmedSvg.replace(
        /(<svg[^>]*)(>)/,
        '$1 xmlns="http://www.w3.org/2000/svg"$2',
      );

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!validateCanvasContext(canvas, ctx)) {
        console.error("Could not get valid canvas context for SVG texture");
        resolve(createSafeFallbackTexture(128));
        return;
      }

      // Reduced canvas size for better performance
      canvas.width = 128;
      canvas.height = 128;

      try {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
      } catch (_error) {
        // Ignore antialiasing errors
      }

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const img = new Image();
      let urlCreated = false;
      let url: string;

      try {
        // Additional security check: ensure the SVG doesn't exceed reasonable size limits
        if (cleanSvg.length > 100000) {
          // 100KB limit
          console.warn("SVG content exceeds size limit, rejecting");
          resolve(createSafeFallbackTexture(128));
          return;
        }

        // Validate that the SVG contains only expected content
        const svgElement = new DOMParser().parseFromString(cleanSvg, "image/svg+xml");
        const parserError = svgElement.querySelector("parsererror");
        if (parserError) {
          console.warn("SVG parsing error detected, rejecting");
          resolve(createSafeFallbackTexture(128));
          return;
        }

        const svgBlob = new Blob([cleanSvg], { type: "image/svg+xml;charset=utf-8" });
        url = URL.createObjectURL(svgBlob);
        urlCreated = true;
      } catch (error) {
        console.error("Error creating SVG blob:", error);
        resolve(createSafeFallbackTexture(128));
        return;
      }

      const cleanup = () => {
        if (urlCreated && url) {
          try {
            URL.revokeObjectURL(url);
          } catch (_error) {
            // Ignore cleanup errors
          }
        }
      };

      const timeout = setTimeout(() => {
        cleanup();
        console.error("SVG texture loading timeout");
        resolve(createSafeFallbackTexture(128));
      }, 10000); // 10 second timeout

      img.onload = () => {
        clearTimeout(timeout);
        try {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, 128, 128);

          // Validate canvas content
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          if (!imageData || !imageData.data || imageData.data.length === 0) {
            throw new Error("Invalid canvas image data after SVG render");
          }

          const texture = new THREE.CanvasTexture(canvas);
          texture.needsUpdate = true;
          texture.generateMipmaps = false;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;

          // Cache the texture
          // Cache the texture for reuse (skip cache for gradients with unique IDs)
          if (!hasUniqueGradient) {
            svgTextureCache.set(trimmedSvg, texture);
          }

          cleanup();
          resolve(texture);
        } catch (error) {
          cleanup();
          console.error("Error processing SVG texture:", error);
          resolve(createSafeFallbackTexture(128));
        }
      };

      img.onerror = () => {
        clearTimeout(timeout);
        cleanup();
        console.error("Failed to load SVG image");
        resolve(createSafeFallbackTexture(128));
      };

      img.src = url;
    } catch (error) {
      console.error("Error creating SVG texture:", error);
      resolve(createSafeFallbackTexture(128));
    }
  });
}

export function createSVGTextureSync(svgString: string): THREE.Texture {
  const SIZE = 128;

  // Sanitize and validate SVG content
  const { isValid, sanitizedSvg } = sanitizeAndValidateSVG(svgString);
  if (!isValid) {
    console.warn("Invalid or unsafe SVG string provided to createSVGTextureSync");
    return createSafeFallbackTexture(SIZE);
  }

  const trimmedSvg = sanitizedSvg;

  try {
    // Additional security check: ensure the SVG doesn't exceed reasonable size limits
    if (trimmedSvg.length > 100000) {
      // 100KB limit
      console.warn("SVG content exceeds size limit, rejecting");
      return createSafeFallbackTexture(SIZE);
    }

    // Validate that the SVG contains only expected content
    const svgElement = new DOMParser().parseFromString(trimmedSvg, "image/svg+xml");
    const parserError = svgElement.querySelector("parsererror");
    if (parserError) {
      console.warn("SVG parsing error detected, rejecting");
      return createSafeFallbackTexture(SIZE);
    }

    const highResSVG = trimmedSvg
      .replace(/width=['"]\d+['"]/, `width='${SIZE}'`)
      .replace(/height=['"]\d+['"]/, `height='${SIZE}'`)
      .replace(/viewBox=['"][^'"]+['"]/, `viewBox='0 0 ${SIZE} ${SIZE}'`);

    const svg = new Blob([highResSVG], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svg);
    const image = new window.Image();
    const texture = new THREE.Texture();
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    // Add timeout for image loading
    const timeout = setTimeout(() => {
      console.error("SVG sync texture loading timeout");
      URL.revokeObjectURL(url);
      // Set fallback texture
      const fallbackTexture = createSafeFallbackTexture(SIZE);
      texture.image = fallbackTexture.image;
      texture.needsUpdate = true;
    }, 5000);

    image.onload = () => {
      clearTimeout(timeout);
      try {
        texture.image = image;
        texture.needsUpdate = true;
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error setting texture image:", error);
        const fallbackTexture = createSafeFallbackTexture(SIZE);
        texture.image = fallbackTexture.image;
        texture.needsUpdate = true;
      }
    };

    image.onerror = () => {
      clearTimeout(timeout);
      // console.error('Error loading SVG image sync');
      URL.revokeObjectURL(url);
      // Set fallback texture
      const fallbackTexture = createSafeFallbackTexture(SIZE);
      texture.image = fallbackTexture.image;
      texture.needsUpdate = true;
    };

    image.src = url;
    return texture;
  } catch (e) {
    console.error("Error creating SVG texture sync:", e);
    return createSafeFallbackTexture(SIZE);
  }
}

export { ICON_TYPES };
